<template>
	<div
		class="offer-ribbon-block"
		:class="{
			[`offer-ribbon-block--subtype-${contentData.attributes?.subtype}`]:
				contentData.attributes?.subtype,
		}"
	>
		<div
			class="offer-ribbon-bg"
			:class="`offer-ribbon-bg--${contentData.attributes?.skin}-406`"
		/>
		<div class="offer-ribbon-block__content">
			<div class="offer-ribbon-block__content-text">
				<ShrinkText
					:text="
						translate(
							contentData.attributes?.promo_text ?? '',
							true,
						)
					"
					:fontSize="14"
					:minFontSize="12"
					:maxLineLimit="2"
					isBold
				/>
			</div>
			<div
				v-if="hasPercentageDiscount"
				class="wax-seal"
				:class="`bg-wax-seal--red-92`"
			>
				<span class="percentage-discount">
					{{ `${contentData.attributes.percentage_discount}% OFF` }}
				</span>
			</div>
			<div
				v-else-if="hasPercentageValue"
				class="wax-seal"
				:class="`bg-wax-seal--red-92`"
			>
				<span class="percentage-value">
					{{ `${contentData.attributes.percentage_value}% VALUE` }}
				</span>
			</div>
			<div
				v-else-if="hasEventFlag"
				class="wax-seal"
				:class="`bg-wax-seal--red-92`"
			>
				<div class="event-special">
					<span>Event</span>
					<span class="event-special__label">special</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import ShrinkText from "@/components/ShrinkText.vue";
import type { ShopItem } from "@/types/shop";
import { translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const hasPercentageDiscount = computed(
	(): boolean =>
		!!contentData.attributes.percentage_discount &&
		contentData.attributes.percentage_discount > 0,
);

const hasPercentageValue = computed(
	(): boolean =>
		!!contentData.attributes.percentage_value &&
		contentData.attributes.percentage_value > 0,
);

const hasEventFlag = computed(
	(): boolean =>
		!!contentData.attributes?.skin &&
		contentData.attributes.skin.includes("event"),
);
</script>

<style lang="scss" scoped>
.offer-ribbon-block {
	position: relative;
	z-index: 3; /* (ribbon > content) = 3 */
	margin-top: -0.75rem;

	&--subtype {
		&-welcome_back_offer {
			margin-top: -1.875rem;

			.offer-ribbon-bg {
				@include background(
					url($path-images + "deeplink/welcome-back/backgrounds/welcome-back-offer-ribbon.avif"),
					100% 100%,
					center,
					no-repeat
				);
				width: 25.625rem;
				height: 8.125rem;
			}

			.offer-ribbon-block {
				&__content {
					top: 1.25rem;
					padding: 0;
					height: calc(100% - 4.375rem);

					&-text {
						padding-left: 5rem;

						:deep(.shrink-text) {
							padding-bottom: 0.125rem;
							text-align: center;
						}
					}
				}
			}
		}
	}

	&__content {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		@include flex-row-between;
		gap: 1.5rem;
		padding-top: 0.625rem;

		&-text {
			position: relative;
			padding-left: 1.75rem;
			width: 80%;
			color: #fff;
			text-align: left;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

			:deep(.text-container) {
				@include flex-row-start;
			}

			:deep(.shrink-text) {
				text-align: left;
			}
		}

		.wax-seal {
			position: relative;
			@include flex-col-center;
			flex-shrink: 0;
			filter: drop-shadow(0 0.188rem 0.75rem rgba(35, 18, 4, 0.6));
			margin-right: -0.75rem;
			padding: 1rem;
			text-align: center;

			span {
				transform: translate(-0.125rem, -0.25rem) rotate(-6deg);
				transform-origin: center;
				color: #fff;
				font-weight: 800;
				font-size: 1.125rem;
				line-height: 1.125rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
			}

			.event-special {
				height: 100%;
				@include flex-col-center;
				flex-wrap: wrap;
				color: #fff;

				&__label {
					font-size: 1rem;
				}
			}
		}
	}
}
</style>
